import { Controller } from '@hotwired/stimulus'
import Sortable from 'sortablejs'

/**
 * Drag-and-drop reordering for the beta tasks views, attached to both the
 * tab strip and the sidebar favorites list.
 *
 * The tab strip has two zone lists (favorites — the tinted "shown in
 * sidebar" block — and non-favorites) sharing a Sortable group, so a tab
 * can be dragged between them: landing in the other zone toggles the
 * view's favorite flag and refreshes the page so the zones and sidebar
 * membership re-render. Same-zone drops PATCH the order and mirror it
 * into the other reorder surfaces without a reload.
 */
export default class extends Controller {
  static targets = ['zone']
  static values = {
    url: String,
    // e.g. /tasks/beta/views/__ID__ — only present on surfaces where a
    // drag can change the favorite zone
    updateUrl: String
  }

  connect() {
    const lists = this.hasZoneTarget ? this.zoneTargets : [this.element]

    this.sortables = lists.map(list => new Sortable(list, {
      group: this.hasZoneTarget ? 'tasks-view-zones' : undefined,
      animation: 150,
      draggable: '.view-tab',
      // Don't start drags from the active tab's controls, and keep their
      // clicks working
      filter: 'button, .dropdown',
      preventOnFilter: false,
      forceFallback: true, // avoid conflicts with Bootstrap dropdowns
      onMove: event => this.allowMove(event),
      onEnd: event => this.handleDrop(event)
    }))
  }

  disconnect() {
    this.sortables?.forEach(sortable => sortable.destroy())
    this.sortables = null
  }

  // Reorder relative to other view tabs, into an empty zone, or after a
  // fixed entry (All Tasks) when crossing zones — but never before it
  allowMove(event) {
    if (event.related === event.to) return true
    if (event.related.classList.contains('view-tab')) return true

    return event.to !== event.from && event.willInsertAfter === true
  }

  handleDrop(event) {
    const dropped = event.item
    const zoneFlag = event.to.dataset.zoneFavorite
    const newFavorite = this.hasUpdateUrlValue && zoneFlag !== undefined ? zoneFlag === 'true' : null

    if (newFavorite === null || String(newFavorite) === dropped.dataset.favorite) {
      this.persistOrder()
      this.mirrorOrder(this.orderedIds(this.element))
      this.resync(this.element)
      return
    }

    // Crossed into the other zone: persist the order, THEN flip the
    // favorite flag — apply_order bumps every row's lock version, so a
    // concurrent favorite update would 409 on the stale record. Re-render
    // the page afterwards (success or not) so it shows the server state.
    dropped.dataset.favorite = String(newFavorite)
    this.persistOrder()
      .then(() => fetch(this.updateUrlValue.replace('__ID__', dropped.dataset.viewId), {
        method: 'PATCH',
        headers: this.requestHeaders(),
        body: JSON.stringify({ saved_form_view: { favorite: newFavorite } })
      }))
      .finally(() => window.Turbo.visit(window.location.href, { action: 'replace' }))
  }

  persistOrder() {
    return fetch(this.urlValue, {
      method: 'PATCH',
      headers: this.requestHeaders(),
      body: JSON.stringify({ ordered_ids: this.orderedIds(this.element) })
    })
  }

  requestHeaders() {
    return {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  orderedIds(container) {
    return Array.from(container.querySelectorAll('.view-tab')).map(tab => tab.dataset.viewId)
  }

  // Replay this surface's order onto the other reorder surfaces (e.g. a
  // tab drag reorders the sidebar list, and vice versa). Each favorite
  // group is reordered in place; ids a surface doesn't show are skipped.
  mirrorOrder(orderedIds) {
    document.querySelectorAll('[data-controller~="tasks-view-reorder"]').forEach(container => {
      if (container === this.element) return

      ['true', 'false'].forEach(favoriteFlag => {
        const group = Array.from(container.querySelectorAll(`.view-tab[data-favorite="${favoriteFlag}"]`))
        if (group.length === 0) return

        const matched = orderedIds
          .map(id => group.find(el => el.dataset.viewId === id))
          .filter(Boolean)
        if (matched.length === 0) return

        const parent = group[0].parentElement
        const anchor = group[group.length - 1].nextElementSibling
        matched.forEach(el => parent.insertBefore(el, anchor))
      })

      this.resync(container)
    })
  }

  // The tab strip's collapse prefix depends on tab order, so recompute it
  // after a drop or a mirror (no-op for the sidebar)
  resync(container) {
    const nav = container.closest('[data-controller~="tasks-tab-overflow"]')
    if (!nav) return

    this.application.getControllerForElementAndIdentifier(nav, 'tasks-tab-overflow')?.sync()
  }
}
